import { Button, Group, Space, Table } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openConfirmModal } from '@mantine/modals';

import { account } from '@mygpt/app';
const { ContentWrap } = account;
import ContainerHeader from '@/components/Headers/ContainerHeader';
import { createTagGroup, deleteTagGroup, getTagGroups, updateTagGroup, useBot } from '@mygpt/app';
import type { QuestionTagGroupOut, QuestionTagGroupPayload } from '@mygpt/types/tag';
import { notifications } from '@mantine/notifications';

import TableBody from './TableBody';
import EditModal from './EditModal';
import classes from './index.module.css';

interface TagGroup {
  id: string;
  name: string;
  tags: string[];
  editTime: string;
}

const TagManagementPage = () => {
  const { t } = useTranslation();
  const { botId } = useBot();

  const [tagGroups, setTagGroups] = useState<TagGroup[]>([]);
  const [initLoading, setInitLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [editingItem, setEditingItem] = useState<TagGroup | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);

  const initData = async () => {
    setInitLoading(true);

    try {
      const { data } = await getTagGroups(botId);

      // 转换API数据为组件所需格式
      const transformedData = data.map(transformApiResponse);
      setTagGroups(transformedData);
    } catch (error) {
      console.error('获取标签组列表失败:', error);
    } finally {
      setInitLoading(false);
    }
  };

  // 类型转换函数：将 API 响应转换为组件使用的格式
  const transformApiResponse = (apiData: QuestionTagGroupOut): TagGroup => {
    return {
      id: apiData.id,
      name: apiData.name,
      tags: apiData.tags.map((tag) => tag.name),
      editTime: dayjs(apiData.updated_at).format('YYYY-MM-DD HH:mm'),
    };
  };

  const handleCreateTagGroup = async () => {
    setCreateLoading(true);
    setUpdateSuccess(false);

    try {
      // 添加临时加载项
      setTagGroups([
        ...tagGroups,
        {
          id: '',
          name: '',
          tags: [],
          editTime: '',
          isLoading: true,
        } as any,
      ]);

      const newItem: TagGroup = {
        id: Date.now().toString(),
        name: '',
        tags: [],
        editTime: dayjs().format('YYYY-MM-DD HH:mm'),
      };

      setEditingItem(newItem);
      setIsCreateMode(true);
      setCreateLoading(false);
      // 重新加载数据
      setTimeout(initData, 350);
    } catch (error) {
      setCreateLoading(false);
      // 移除临时加载项
      setTagGroups(tagGroups.filter((item) => !(item as any).isLoading));
    }
  };

  const handleDeleteTagGroup = async (item: TagGroup) => {
    openConfirmModal({
      title: t('tag.delete_group'),
      centered: true,
      children: <span>{t('tag.delete_group_confirm', { name: item.name })}</span>,
      labels: { confirm: t('tag.delete'), cancel: t('tag.cancel') },
      confirmProps: {
        color: 'red',
        loading: deleteLoading,
      },
      onConfirm: async () => {
        setDeleteLoading(true);
        try {
          await deleteTagGroup(botId, item.id);

          setTagGroups(tagGroups.filter((group) => group.id !== item.id));

          notifications.show({
            message: t('tag.delete_success_message'),
            color: 'green',
          });
        } catch (error) {
          console.error('删除标签组失败:', error);
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  const handleUpdateTagGroup = async (currentTagInput: string) => {
    if (!editingItem) return;

    if (currentTagInput && editingItem.tags.length < 10) {
      // 如果当前输入框有值，且标签数量小于10，则将当前输入框的值也追加到标签组中。去重。
      editingItem.tags = Array.from(new Set([...editingItem.tags, currentTagInput]));
    }

    // 保存时统一去除首尾空白（输入过程中允许空格，见 EditModal handleGroupNameChange）
    const groupName = editingItem.name.trim();

    if (groupName.length === 0) {
      notifications.show({
        message: t('tag.no_group_name_error_message'),
        color: 'red',
      });
      return;
    }

    if (editingItem.tags.length === 0) {
      notifications.show({
        title: t('tag.no_tags_error_title'),
        message: t('tag.no_tags_error_message'),
        color: 'red',
      });
      return;
    }

    setUpdateLoading(true);

    try {
      const payload: QuestionTagGroupPayload = {
        name: groupName,
        tags: editingItem.tags,
      };

      let apiResponse: QuestionTagGroupOut;

      if (isCreateMode) {
        const { data } = await createTagGroup(botId, payload);
        apiResponse = data;
      } else {
        const { data } = await updateTagGroup(botId, editingItem.id, payload);
        apiResponse = data;
      }

      const transformedData = transformApiResponse(apiResponse);

      if (isCreateMode) {
        setTagGroups([transformedData, ...tagGroups.filter((item) => !(item as any).isLoading)]);
      } else {
        setTagGroups(tagGroups.map((item) => (item.id === editingItem.id ? transformedData : item)));
      }

      setUpdateLoading(false);
      setUpdateSuccess(true);
      setEditingItem(null);

      notifications.show({
        message: isCreateMode ? t('tag.create_success_message') : t('tag.update_success_message'),
        color: 'green',
      });
    } catch (error) {
      console.error('保存标签组失败:', error);
      setUpdateLoading(false);
    }
  };

  const handleEditClick = (item: TagGroup) => {
    setEditingItem({ ...item });
    setIsCreateMode(false);
  };

  const handleDeleteClick = (item: TagGroup) => {
    handleDeleteTagGroup(item);
  };

  // 获取所有现有标签的函数
  const getAllExistingTags = () => {
    const allTags = new Set<string>();
    tagGroups
      .filter((group) => !(group as any).isLoading) // 排除临时加载项
      .forEach((group) => {
        group.tags.forEach((tag) => {
          allTags.add(tag.toLowerCase().trim()); // 统一转换为小写并去除空白符进行比较
        });
      });
    return Array.from(allTags);
  };

  // 获取其他标签组的标签（编辑模式下排除当前组）
  const getOtherGroupTags = (currentGroupId?: string) => {
    const otherTags = new Set<string>();
    tagGroups
      .filter((group) => !(group as any).isLoading && group.id !== currentGroupId)
      .forEach((group) => {
        group.tags.forEach((tag) => {
          otherTags.add(tag.toLowerCase().trim());
        });
      });
    return Array.from(otherTags);
  };

  // 获取所有现有组名的函数
  const getAllExistingGroupNames = () => {
    const allGroupNames = new Set<string>();
    tagGroups
      .filter((group) => !(group as any).isLoading) // 排除临时加载项
      .forEach((group) => {
        if (group.name && group.name.trim()) {
          allGroupNames.add(group.name.toLowerCase().trim()); // 统一转换为小写并去除空白符进行比较
        }
      });
    return Array.from(allGroupNames);
  };

  // 获取其他标签组的组名（编辑模式下排除当前组）
  const getOtherGroupNames = (currentGroupId?: string) => {
    const otherGroupNames = new Set<string>();
    tagGroups
      .filter((group) => !(group as any).isLoading && group.id !== currentGroupId)
      .forEach((group) => {
        if (group.name && group.name.trim()) {
          otherGroupNames.add(group.name.toLowerCase().trim());
        }
      });
    return Array.from(otherGroupNames);
  };

  useEffect(() => {
    initData();
  }, []);

  return (
    <>
      <ContainerHeader title={t('tag.container_title')} description={t('tag.container_description')} />
      <Group justify="flex-end" className="mb-5">
        <Button
          variant="gradient"
          gradient={{ from: '#6d28d9', to: '#be81ff' }}
          leftSection={<IconPlus />}
          loading={createLoading || initLoading}
          onClick={handleCreateTagGroup}
        >
          {t('tag.create_group')}
        </Button>
      </Group>
      <ContentWrap className="p-5">
        <Table verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={classes.borderBottomColor}>
                <span className="text-gray-400 font-light">{t('tag.group')}</span>
              </Table.Th>
              <Table.Th className={`${classes.borderBottomColor} w-2/4`}>
                <span className="text-gray-400 font-light">{t('tag.tags')}</span>
              </Table.Th>
              <Table.Th className={classes.borderBottomColor}>
                <span className="text-gray-400 font-light">{t('tag.edit_time')}</span>
              </Table.Th>
              <Table.Th className={classes.borderBottomColor}>
                <span className="text-gray-400 font-light">{t('tag.action')}</span>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <TableBody tagGroups={tagGroups} onEdit={handleEditClick} onDelete={handleDeleteClick} />
        </Table>
        <Space h="xl" />
      </ContentWrap>

      {/* 编辑/创建模态框 */}
      <EditModal
        opened={!!editingItem}
        onClose={() => setEditingItem(null)}
        editingItem={editingItem}
        isCreateMode={isCreateMode}
        updateLoading={updateLoading}
        onSave={handleUpdateTagGroup}
        onEditingItemChange={setEditingItem}
        existingTags={isCreateMode ? getAllExistingTags() : getOtherGroupTags(editingItem?.id)}
        existingGroupNames={isCreateMode ? getAllExistingGroupNames() : getOtherGroupNames(editingItem?.id)}
      />
    </>
  );
};

export default TagManagementPage;
